import React from 'react';
import { useCallback } from 'react';
import { useState, useEffect } from 'react';
import { isNullOrEmptyArray } from 'src/util/arrays';
import { GenericModal } from "../../components/Modal";
import GenericCollapsibleList from '../../sections/GenericCollapsibleList';
import * as GenericUi from '../../components/Modal/styles';
import { InfoIconEvolutive } from 'src/components/Icons';
import { InputText } from 'src/components';
import useFlashMessage from 'src/hooks/FlashMessage';
import { DELETED_QUESTION, DELETED_QUESTION_AND_DISSOLVE_GROUP, EDIT_COMPLETED } from '../../util/Constantes';
import CrossQuestionsAlternatives from '../../widget/CrossQuestionsAlternatives';

const itemProps = {
  width: "100%",
  withTag: true,
  maxHeight: "unset",
  renderStyle: "combinedList"
};

export const CombinationModal = function ({
  groupValues = [],
  groupTitle,
  onSave,
  onCancel,
  customerId,

  // variável que exibe a opção de remover os items
  withRemovableItems,
  ...props
}) {
  // states
  const [combinationTitle, setCombinationTitle] = useState('');
  const [internalValues, setInternalValues] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [removedItems, setRemovedItems] = useState([]);

  //hooks
  const [ handleFlashMessage ] = useFlashMessage();

  /**
   * Effects: Seta o valor inicial do grupo de perguntas combinadas selecionados
   */
  useEffect(() => {
    if (!isNullOrEmptyArray(groupValues)) {
      setInternalValues([...groupValues]);
    }
  }, [groupValues]);


  /**
   * Callbacks: Chama o widget para listagem de alternativas
   */
  const Children = useCallback((childrenProps) => {
    const { isOpen } = childrenProps;
    const [sid, qid] = childrenProps.id.split("_");

    return (
      <CrossQuestionsAlternatives
        surveyId={sid}
        questionId={qid}
        customerId={customerId}
        isOpen={isOpen}
        listOnlyAlternatives
      />
    );
  }, []);

  /**
   * Callbacks: Seta o novo estado de seleção dos itens
   */
  const handleSelectionChange = useCallback((sel) => {
    setSelectedItems([...sel]);
  }, [setSelectedItems]);

  /**
   * Callbacks: Seta o estado do título
   */
  const handleCombinationTitleChange = useCallback((newTextValue) => {
    setCombinationTitle(newTextValue);
  }, [setCombinationTitle]);

  /**
   * Callbacks: Seta o estado dos itens removidos.
   */
  const handleOnRemoveItem = useCallback((items) => {
    setRemovedItems([...items]);
  }, [setRemovedItems]);

  /**
   * Callbacks: Atualiza os valores internos levando em conta os itens removidos.
   */
  const getFilteredInternalValues = useCallback(() => {
    if (!removedItems.length) return internalValues;
    return internalValues.filter(item => !removedItems.some(({ id }) => id === item.id));
  }, [internalValues, removedItems]);


  /**
   * Identifica quando ocorrer uma remoção no array de perguntas combinadas para notificar a alteração
   * Se o tamanho do grupo for menor do que 2 a combinação é desfeita e a modal fechada.
   */
  useEffect(() => {
    if(removedItems.length > 0) {
      handleFlashMessage(
        DELETED_QUESTION.title,
        DELETED_QUESTION.message, 
        "success"
      );

      const newGroup = getFilteredInternalValues();
      if(newGroup.length < 2) {
        onOk();
      }
    }
  }, [removedItems])


  /**
   * Callbacks: Renderiza a listagem de perguntas combinadas
   */
  const renderGroupItems = useCallback(() => {
    return (
      <GenericCollapsibleList
        data={getFilteredInternalValues()}
        withExpansibleItems
        // withControlledItems
        withRemovableItems={withRemovableItems}
        // itemCheckboxStyle={'square'}
        onChangeSelection={handleSelectionChange}
        customItemProps={itemProps}
        onRemoveItem={handleOnRemoveItem}
        ChildrenComponent={Children}
      />
    )
  }, [internalValues, removedItems]);

  const onClose = useCallback(() => {
    if (onCancel) onCancel();
  }, [onCancel]);

  const onOk = useCallback(() => {
    const result = removedItems.length ? getFilteredInternalValues() : internalValues;
    const finalConfig = {
      title: combinationTitle || groupTitle,
      questions: [...result],
    }

    if (onSave) onSave(finalConfig);
    if(finalConfig.questions.length < 2) {
      handleFlashMessage(
        DELETED_QUESTION_AND_DISSOLVE_GROUP.title,
        DELETED_QUESTION_AND_DISSOLVE_GROUP.message, 
        "success"
      );
    } else {
        handleFlashMessage(
          EDIT_COMPLETED.title,
          EDIT_COMPLETED.message,
          "success"
        );
    }

  }, [onSave, combinationTitle, internalValues, removedItems]);

  return (
    <GenericModal
      title={'Novo grupo de perguntas combinadas'}
      onCancel={onClose}
      onOk={onOk}
      isOpen
      tooltipTitle="Customize o Título da combinação, visualize alternativas das perguntas e remova perguntas combinadas do grupo"
      okBtnTitle="SALVAR"
      cancelBtnTitle="CANCELAR"
    >
      <GenericUi.FixedItem>
        <GenericUi.Label withIcon>
          <InfoIconEvolutive />
          {'Titulo da combinação'}
        </GenericUi.Label>
        <InputText 
          value={combinationTitle}
          onChange={handleCombinationTitleChange}
          defaultValue={groupTitle || (groupValues && groupValues[0].mainTitle)}
          placeholder={'INFORME O NOVO NOME'}
        />
      </GenericUi.FixedItem>
      <GenericUi.FixedItem>
        <GenericUi.Label withIcon>
          <InfoIconEvolutive />
          {'Escolha uma combinação para adicionar a pergunta selecionada'}
        </GenericUi.Label>
        {renderGroupItems()}
      </GenericUi.FixedItem>
    </GenericModal>
  )
}